import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from './RichTextEditor';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  FileText,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Award,
  CreditCard,
  User,
  Loader2,
  Plus,
  Trash2,
  TrendingUp,
  Activity,
  Sparkles,
  Minus,
  Edit2,
  Building2,
  CalendarDays,
  FolderOpen
} from 'lucide-react';
import { useTheme } from '@mui/material';
import AbsenceRecordsModal from './AbsenceRecordsModal';
// ✅ IMPORT BOTH functions
import { getMyProfile, getEmployeeImage, updateMyProjects, updateMyPositions } from '../services/HRstaff';
import { getMyContract } from '../services/ContractAPI';
import { getEmployeeLeaveBalance } from '../services/leavesAPIs';
import { getMyAbsences } from '../services/absenceAPIs';
import { getMyRequests } from '../services/requestAPIs';
import { getMyDocuments } from '../services/documentAPIs';
import {
  getEmployeeLatenessSummaries,
  getEmployeeSicknessSummaries,
  getEmployeePersonalCommitmentsSummaries
} from '../services/absenceAPIs';
import { getAllProjects } from '../services/projectManagerAPIs';
import { getMyCareer, createMyCareer, updateMyCareer, deleteMyCareer } from '../services/careerAPIs';
import { calculateDaysWorked, calculateMonthsWorked, calculateTotalMonths, formatMonthsWorked } from './dateCalculations';
import Evaluation from './Evaluation';

// ✅ SOFT SKY-BLUE THEME
const THEME_PRIMARY = '#0EA5E9';
const THEME_SECONDARY = '#0284C7';
const SKY_PRIMARY = '#93C5FD';
const SKY_SECONDARY = '#60A5FA';

// FTE options
const FTE_OPTIONS = [0.25, 0.5, 0.75, 1.0];

// Position options
const POSITION_OPTIONS = [
  'Account Manager', 'Bookkeeper', 'Client Adviser', 'Customer Service',
  'Developer', 'Director', 'Facility', 'HR', 'HR Manager', 'IT Professional',
  'Manager', 'Marketing', 'Personal Assistance', 'Planner', 'Project Manager',
  'Quality Reviewer', 'Software Engineer', 'HQ'
];

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (err) {
    return 'Invalid Date';
  }
};

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [displayValue, setDisplayValue] = useState("0,00");

  useEffect(() => {
    let startTime;
    let animationFrame;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      const currentNumber = percentage * value;
      const formattedString = currentNumber.toFixed(2).replace('.', ',');
      setDisplayValue(formattedString);
      if (percentage < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);
  return <>{displayValue}</>;
};

const isBirthdayToday = (dob) => {
  if (!dob) return false;

  const today = new Date();
  const target = new Date(dob);

  if (!Number.isNaN(target.getTime())) {
    return target.getDate() === today.getDate() && target.getMonth() === today.getMonth();
  }

  // Fallback for DD-MM-YYYY style values
  if (typeof dob === 'string') {
    const ddmmyyyy = dob.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (ddmmyyyy) {
      const [, dd, mm] = ddmmyyyy;
      return Number(dd) === today.getDate() && Number(mm) - 1 === today.getMonth();
    }
  }

  return false;
};

const getBirthdayCelebrationKey = (employeeId) => {
  const today = new Date().toISOString().slice(0, 10);
  return `birthday-celebration-${employeeId}-${today}`;
};

const MyDashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [contract, setContract] = useState(null);
  const [activeTab, setActiveTab] = useState('attendance');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latenessRate, setLatenessRate] = useState(0);
  const [latenessOver10Rate, setLatenessOver10Rate] = useState(0);
  const [sicknessRate, setSicknessRate] = useState(0);
  const [personalRate, setPersonalRate] = useState(0);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [vacationDaysLeft, setVacationDaysLeft] = useState(null);
  const [sickDays, setSickDays] = useState(0);
  const [personalDays, setPersonalDays] = useState(0);
  const [latenessDays, setLatenessDays] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState('All');
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showBirthdayCelebration, setShowBirthdayCelebration] = useState(false);

  // Projects & Positions State
  const [projects, setProjects] = useState({ project1: 'N/A', project2: 'N/A' });
  const [positions, setPositions] = useState([{ id: null, positionName: '', fte: 1.0 }]);
  const [showProject2, setShowProject2] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [selectedAdditionalProjects, setSelectedAdditionalProjects] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [projectOptions, setProjectOptions] = useState(["N/A", "FL"]);

  // Career State
  const CAREER_POSITION_OPTIONS = [
    'Account Manager', 'Bookkeeper', 'Client Adviser', 'Customer Service',
    'Developer', 'Director', 'Facility', 'HR', 'HR Manager', 'IT Professional',
    'Manager', 'Marketing', 'Personal Assistance', 'Planner', 'Project Manager',
    'Quality Reviewer', 'Software Engineer', 'HQ',
  ];
  const CAREER_PROJECT_OPTIONS = [
    'N/A', 'MM', 'LC', 'IT', 'FF', 'FL', 'MKT', 'HA', 'CS', 'HR', 'HR-S',
    'H2D', 'WLF', 'HBL', 'MC', 'TEDS', 'QR', 'MA', 'PL', 'WAL',
    'SIPI', 'Lineo', 'CB', 'UTB', 'AP', 'AXP', 'ENTRPNR',
  ];
  const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Internship', 'Contract', 'Freelance', 'Consultant'];
  const emptyCareerForm = {
    positionTitle: '',
    department: '',
    startDate: '',
    endDate: '',
    employmentType: 'Full-time',
    description: '',
    projectNames: '',
  };
  const typeColors = {
    'Full-time': { bg: '#DBEAFE', text: '#1D4ED8' },
    'Part-time': { bg: '#FEF3C7', text: '#92400E' },
    'Internship': { bg: '#D1FAE5', text: '#065F46' },
    'Contract': { bg: '#EDE9FE', text: '#5B21B6' },
    'Freelance': { bg: '#FCE7F3', text: '#9D174D' },
    'Consultant': { bg: '#E0F2FE', text: '#0369A1' },
  };
  const [careers, setCareers] = useState([]);
  const [careersLoading, setCareersLoading] = useState(false);
  const [careersError, setCareersError] = useState(null);
  const [careersLoaded, setCareersLoaded] = useState(false);
  const [showCareerForm, setShowCareerForm] = useState(false);
  const [editingCareer, setEditingCareer] = useState(null);
  const [careerForm, setCareerForm] = useState(emptyCareerForm);
  const [allDepartments, setAllDepartments] = useState([]);
  const [careerDropdownsLoaded, setCareerDropdownsLoaded] = useState(false);

  const calculateTotalFTE = (currentPositions) => {
    return currentPositions.reduce((sum, pos) => sum + (parseFloat(pos.fte) || 0), 0);
  };

  const isFTEValid = (currentPositions) => {
    const total = calculateTotalFTE(currentPositions);
    return total > 0 && total <= 1.001;
  };

  useEffect(() => {
    const loadMainProfileImage = async () => {
      if (employee && employee.hasImage) {
        try {
          const imageUrl = await getEmployeeImage(employee.id);
          setProfileImageUrl(imageUrl);
        } catch (error) {
          console.error('Failed to load main profile image:', error);
          setProfileImageUrl(null);
        }
      } else {
        setProfileImageUrl(null);
      }
    };
    loadMainProfileImage();
    return () => {
      if (profileImageUrl) URL.revokeObjectURL(profileImageUrl);
    };
  }, [employee?.id, employee?.hasImage]);

  // Fetch main employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [employeeData, absences, employeeContract, requests] = await Promise.all([
          getMyProfile(),
          getMyAbsences(),
          getMyContract(),
          getMyRequests()
        ]);
        const leaveBalanceData = await getEmployeeLeaveBalance(employeeData.employeeId);

        const normalizedRequests = requests.map(req => ({
          id: req.requestId || req.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
          type: req.type || 'UNKNOWN',
          status: req.status || 'PENDING',
          details: req.details || req.description || 'N/A',
          date: req.date || req.submissionDate || req.createdAt || new Date(),
          document: req.document || null,
        }));

        const joinDate = employeeData.joinDate || employeeData.dateOfJoining;
        if (!joinDate) throw new Error('Join date is required');

        const monthsWorked = calculateMonthsWorked(joinDate);
        const daysWorked = calculateDaysWorked(joinDate);

        const calculateWorkDays = (startDate) => {
          const start = new Date(startDate);
          const end = new Date();
          let count = 0;
          let current = new Date(start);
          while (current <= end) {
            const day = current.getDay();
            if (day !== 0 && day !== 6) count++;
            current.setDate(current.getDate() + 1);
          }
          return count;
        };

        const sicknessData = absences.filter(a => a.ty_pe?.toUpperCase() === 'SICKNESS');
        const sickLeaveCount = sicknessData.reduce((sum, a) => sum + (a.days || a.durationInDays || 0), 0);
        const personalData = absences.filter(
          a => a.ty_pe?.toUpperCase() === 'PERSONAL_COMMITMENT' || a.ty_pe?.toUpperCase() === 'PERSONAL'
        );
        const personalLeaveCount = personalData.length;
        const latenessData = absences.filter(a => a.ty_pe?.toUpperCase() === 'LATENESS');
        const latenessCount = latenessData.length;
        const totalWorkDaysEmployed = calculateWorkDays(joinDate);
        const actualWorkedDays = totalWorkDaysEmployed;
        const vacationDaysLeft = leaveBalanceData?.ANNUAL_LEAVE ?? 0;

        // --- PROJECTS LOGIC ---
        const employeeProjects = Array.isArray(employeeData.projects)
          ? employeeData.projects.map(p => typeof p === 'string' ? p : p.projectName)
          : [];

        const fetchedProjects = {
          project1: employeeProjects[0] || 'N/A',
          project2: employeeProjects.slice(1).join('-') || 'N/A'
        };
        const initialAdditionalProjects = employeeProjects.slice(1);

        // --- POSITIONS LOGIC ---
        let loadedPositions = [];
        if (Array.isArray(employeeData.positions) && employeeData.positions.length > 0) {
          loadedPositions = employeeData.positions.map(p => ({
            id: p.id || p.positionId || null,
            positionName: p.positionName || '',
            fte: p.fte != null ? Number(p.fte) : 1.0
          }));
        } else {
          loadedPositions = [{ id: null, positionName: employeeData.position || '', fte: 1.0 }];
          if (employeeData.secondposition && employeeData.secondposition.trim() !== '') {
            loadedPositions.push({ id: null, positionName: employeeData.secondposition, fte: 0.5 });
          }
        }

        const displayPositionString = loadedPositions
          .filter(p => p.positionName)
          .map(p => p.positionName)
          .join(' & ');

        const transformedEmployee = {
          id: employeeData.employeeId,
          name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() || 'N/A',
          firstName: employeeData.firstName || '',
          lastName: employeeData.lastName || '',
          hasImage: !!employeeData.image,
          position: displayPositionString || employeeData.position || 'N/A',
          status: employeeData.status || 'Active',
          joinDate: joinDate,
          company: employeeData.company || 'N/A',
          personalInfo: { location: employeeData.office?.name || 'Unknown', office: employeeData.office?.name || 'N/A' },
          user: { email: employeeData.email || 'N/A', role: employeeData.user?.email || 'N/A' },
          cnss: employeeData.cnss || 'N/A',
          cin: employeeData.cin || 'N/A',
          gender: employeeData.gender || 'N/A',
          phone: employeeData.phone || 'N/A',
          dob: employeeData.dob || null,
          diploma: employeeData.diploma || 'N/A',
          office: employeeData.office?.name || employeeData.officeName || 'N/A',
          requests: normalizedRequests,
          timeOverview: {
            totalMonths: Math.round(monthsWorked),
            workedMonths: Math.round(monthsWorked),
            workedDays: daysWorked,
            actualWorkedDays: actualWorkedDays,
            totalDaysEmployed: totalWorkDaysEmployed,
            formattedDuration: formatMonthsWorked(joinDate),
          },
        };

        setEmployee(transformedEmployee);
        setVacationDaysLeft(vacationDaysLeft);
        setSickDays(sickLeaveCount);
        setPersonalDays(personalLeaveCount);
        setLatenessDays(latenessCount);
        setContract(employeeContract || null);
        setProjects(fetchedProjects);
        setPositions(loadedPositions);
        setSelectedAdditionalProjects(initialAdditionalProjects);
        setShowProject2(initialAdditionalProjects.length > 0);

        const birthdayToday = isBirthdayToday(transformedEmployee.dob);
        const celebrationKey = getBirthdayCelebrationKey(transformedEmployee.id || 'me');
        const alreadyShownToday = localStorage.getItem(celebrationKey) === '1';
        setShowBirthdayCelebration(birthdayToday && !alreadyShownToday);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load your profile data.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchEmployeeData();

    const loadProjects = async () => {
      try {
        const data = await getAllProjects();
        if (Array.isArray(data)) {
          const names = data.map(p => p.projectName).filter(Boolean);
          setProjectOptions(["N/A", ...new Set([...names, "FL"])]);
        }
      } catch (err) {
        console.error("Failed to load project options:", err);
      }
    };
    loadProjects();
  }, []);

  // Fetch career records + departments
  useEffect(() => {
    const fetchCareerData = async () => {
      setCareersLoading(true);
      setCareersError(null);
      try {
        const data = await getMyCareer();
        setCareers(Array.isArray(data) ? data : []);
        setCareersLoaded(true);
      } catch (err) {
        console.error('Failed to fetch career records:', err);
        setCareersError('Failed to load career records.');
      } finally {
        setCareersLoading(false);
      }
    };
    if (employee?.id && !careersLoaded) {
      fetchCareerData();
    }
  }, [employee?.id, careersLoaded]);

  // Fetch departments for career form
  useEffect(() => {
    const fetchDepartments = async () => {
      if (careerDropdownsLoaded) return;
      try {
        const api = (await import('../api')).default;
        const { API_URL } = await import('../config');
        const response = await api.get(`${API_URL}/departments`);
        setAllDepartments(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to fetch departments:', err);
        setAllDepartments([]);
      } finally {
        setCareerDropdownsLoaded(true);
      }
    };
    fetchDepartments();
  }, [careerDropdownsLoaded]);

  // Career form handlers
  const handleCareerFormChange = (e) => {
    const { name, value } = e.target;
    setCareerForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCareerSubmit = async () => {
    try {
      if (editingCareer) {
        await updateMyCareer(editingCareer.id, careerForm);
      } else {
        await createMyCareer(careerForm);
      }
      const data = await getMyCareer();
      setCareers(Array.isArray(data) ? data : []);
      setCareerForm(emptyCareerForm);
      setEditingCareer(null);
      setShowCareerForm(false);
    } catch (err) {
      setCareersError(err.message || 'Failed to save career entry.');
    }
  };

  const handleCareerEdit = (career) => {
    setEditingCareer(career);
    setCareerForm({
      positionTitle: career.positionTitle || '',
      department: career.department || '',
      startDate: career.startDate || '',
      endDate: career.endDate || '',
      employmentType: career.employmentType || 'Full-time',
      description: career.description || '',
      projectNames: career.projectNames || '',
    });
    setShowCareerForm(true);
  };

  const handleCareerDelete = async (careerId) => {
    if (!window.confirm('Delete this career record?')) return;
    try {
      await deleteMyCareer(careerId);
      setCareers(prev => prev.filter(c => c.id !== careerId));
    } catch (err) {
      setCareersError(err.message || 'Failed to delete career entry.');
    }
  };

  const handleCareerCancel = () => {
    setShowCareerForm(false);
    setEditingCareer(null);
    setCareerForm(emptyCareerForm);
  };
  useEffect(() => {
    const fetchAbsenceRates = async () => {
      if (!employee?.id) return;
      setRatesLoading(true);
      try {
        const latenessData = await getEmployeeLatenessSummaries();
        const sicknessData = await getEmployeeSicknessSummaries();
        const personalData = await getEmployeePersonalCommitmentsSummaries();
        const latenessEmp = latenessData.find(emp => emp.employeeId === employee.id);
        const sicknessEmp = sicknessData.find(emp => emp.employeeId === employee.id);
        const personalEmp = personalData.find(emp => emp.employeeId === employee.id);

        const latenessOver10Count = latenessEmp ? (latenessEmp.latenessOver10Count || 0) : 0;
        const totalWorkedDaysLateness = latenessEmp ? (latenessEmp.totalWorkedDays || 0) : 0;
        const over10Rate = totalWorkedDaysLateness > 0 ? parseFloat(((latenessOver10Count / totalWorkedDaysLateness) * 100).toFixed(2)) : 0;

        setLatenessRate(latenessEmp ? parseFloat(latenessEmp.absenceRate) : 0);
        setLatenessOver10Rate(over10Rate);
        setSicknessRate(sicknessEmp ? parseFloat(sicknessEmp.absenceRate) : 0);
        setPersonalRate(personalEmp ? parseFloat(personalEmp.absenceRate) : 0);
      } catch (err) {
        console.error('Failed to fetch absence rates:', err);
      } finally {
        setRatesLoading(false);
      }
    };
    fetchAbsenceRates();
  }, [employee?.id]);

  useEffect(() => {
    if (!showBirthdayCelebration || !employee?.id) return;

    const autoCloseTimer = setTimeout(() => {
      localStorage.setItem(getBirthdayCelebrationKey(employee.id), '1');
      setShowBirthdayCelebration(false);
    }, 9000);

    return () => clearTimeout(autoCloseTimer);
  }, [showBirthdayCelebration, employee?.id]);

  // Fetch documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const myDocs = await getMyDocuments();
        setDocuments(myDocs || []);
      } catch (err) {
        setDocuments([]);
      }
    };
    if (employee?.id) {
      fetchDocuments();
    }
  }, [employee?.id]);

  const handleOpenAbsenceModal = (absenceType) => {
    setSelectedAbsenceType(absenceType);
    setShowAbsenceModal(true);
  };

  const handleCloseAbsenceModal = () => {
    setShowAbsenceModal(false);
    setSelectedAbsenceType(null);
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesType = selectedDocumentType === 'All' ? true : doc.type === selectedDocumentType;
    const matchesSearch = docSearchTerm === '' || (doc.description?.toLowerCase().includes(docSearchTerm.toLowerCase()));
    return matchesType && matchesSearch;
  });

  const handleProjectChange = (projectKey, value) => {
    setProjects(prev => ({ ...prev, [projectKey]: value }));
  };

  // Position Handlers
  const handlePositionChange = (index, field, value) => {
    setPositions(prev => {
      const newPositions = [...prev];
      newPositions[index] = { ...newPositions[index], [field]: value };
      return newPositions;
    });
  };

  const handleAddPosition = () => {
    if (positions.length >= 4) return;
    setPositions(prev => [...prev, { id: null, positionName: '', fte: 0.25 }]);
  };

  const handleRemovePosition = (index) => {
    setPositions(prev => prev.filter((_, i) => i !== index));
  };

  // ✅ FIXED SAVE LOGIC: USES BOTH ENDPOINTS
  const handleSaveProjects = async () => {
    // 1. Prepare Positions Payload
    const validPositions = positions
      .filter(p => p.positionName && p.positionName.trim() !== '')
      .map(p => ({
        id: p.id || null,
        positionName: p.positionName,
        fte: Number(p.fte)
      }));

    if (!isFTEValid(validPositions)) {
      setProjectsError("Total FTE must be between 0 and 1.0");
      return;
    }

    setIsProjectsLoading(true);
    setProjectsError(null);
    setSaveSuccess(false);

    try {
      const multiProjectsString = (selectedAdditionalProjects || []).join('-');
      const allProjects = [projects.project1, ...selectedAdditionalProjects].filter(p => p && p !== 'N/A');
      const projectData = {
        projectNames: allProjects, // Matches ProjectUpdateDto
      };

      const positionData = {
        positions: validPositions
      };

      // 3. Call both endpoints in parallel
      const [projectResponse, employeeResponse] = await Promise.all([
        updateMyProjects(projectData),
        updateMyPositions(positionData)
      ]);

      // 4. Update local state
      setProjects(prev => ({ ...prev, project2: multiProjectsString }));

      const newDisplayPosition = validPositions.map(p => p.positionName).join(' & ');
      setEmployee(prev => ({ ...prev, position: newDisplayPosition }));

      // Sync positions from the employee response (to capture new IDs)
      if (employeeResponse && employeeResponse.positions) {
        setPositions(employeeResponse.positions.map(p => ({
          id: p.id || p.positionId,
          positionName: p.positionName,
          fte: p.fte
        })));
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (err) {
      console.error("Error updating profile:", err);
      setProjectsError(err.response?.data?.message || err.message || "Failed to update profile.");
    } finally {
      setIsProjectsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="axo-page-loader">
        <div className="axo-page-loader__ring" />
        <p className="axo-page-loader__text">Loading your profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <AlertCircle size={48} color="#EF4444" />
        <p>{error}</p>
        <button onClick={() => window.location.reload()} style={{
          padding: '0.5rem 1rem',
          backgroundColor: THEME_PRIMARY,
          color: 'white',
          border: 'none',
          borderRadius: '0.5rem',
          cursor: 'pointer'
        }}>Retry</button>
      </div>
    );
  }

  if (!employee) return null;

  const closeBirthdayCelebration = () => {
    if (employee?.id) {
      localStorage.setItem(getBirthdayCelebrationKey(employee.id), '1');
    }
    setShowBirthdayCelebration(false);
  };

  return (
    <div style={{
      minHeight: 'auto',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: isDark ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
    }}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animated-card {
          transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .animated-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 24px rgba(14, 165, 233, 0.18) !important;
        }
        .glass-effect {
          background: isDark ? 'rgba(30, 41, 59, 0.78)' : 'rgba(255, 255, 255, 0.78)';
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)'};
          border-radius: inherit;
        }
        .gradient-text {
          background: linear-gradient(135deg, ${THEME_PRIMARY} 0%, ${THEME_SECONDARY} 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .collapsible-group {
          overflow: hidden;
          transition: max-height 0.3s ease-out, opacity 0.3s ease-out;
        }
        .collapsible-group.collapsed {
          max-height: 0;
          opacity: 0;
        }
        @keyframes birthdayPopIn {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes confettiFall {
          0% { transform: translateY(-16vh) rotate(0deg); opacity: 0; }
          12% { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.2; }
        }
        .birthday-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          pointer-events: none;
          background: radial-gradient(circle at 20% 20%, rgba(14,165,233,0.14), transparent 45%),
                      radial-gradient(circle at 80% 25%, rgba(245,158,11,0.12), transparent 45%),
                      radial-gradient(circle at 50% 80%, rgba(16,185,129,0.12), transparent 50%);
        }
        .birthday-confetti {
          position: absolute;
          top: -18vh;
          width: 8px;
          height: 20px;
          border-radius: 4px;
          animation: confettiFall linear forwards;
        }
        .birthday-card {
          pointer-events: auto;
          width: min(560px, 92vw);
          margin: 14vh auto 0;
          border-radius: 20px;
          padding: 1.35rem 1.4rem;
          animation: birthdayPopIn 420ms ease-out;
          background: ${isDark ? 'rgba(15, 23, 42, 0.94)' : 'rgba(255, 255, 255, 0.95)'};
          border: 1px solid ${isDark ? 'rgba(148,163,184,0.3)' : 'rgba(14,165,233,0.2)'};
          box-shadow: 0 20px 50px rgba(2, 132, 199, 0.22);
          backdrop-filter: blur(10px);
        }
        @media (prefers-reduced-motion: reduce) {
          .birthday-confetti {
            animation: none;
            opacity: 0;
          }
          .birthday-card {
            animation: none;
          }
        }
      `}</style>

      {showBirthdayCelebration && (
        <div className="birthday-overlay" role="dialog" aria-live="polite" aria-label="Birthday celebration">
          {Array.from({ length: 28 }).map((_, idx) => {
            const colors = ['#0EA5E9', '#22C55E', '#F59E0B', '#A855F7', '#EF4444'];
            const left = `${(idx * 3.7) % 100}%`;
            const duration = `${4.4 + (idx % 7) * 0.55}s`;
            const delay = `${(idx % 8) * 0.16}s`;
            return (
              <span
                key={`confetti-${idx}`}
                className="birthday-confetti"
                style={{
                  left,
                  background: colors[idx % colors.length],
                  animationDuration: duration,
                  animationDelay: delay
                }}
              />
            );
          })}

          <div className="birthday-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem' }}>
              <div>
                <div className="gradient-text" style={{ fontWeight: 800, fontSize: '1.25rem', marginBottom: '0.25rem' }}>
                  🎉 Happy Birthday, {employee.firstName || employee.name}!
                </div>
                <p style={{ margin: 0, color: isDark ? '#cbd5e1' : '#374151', fontSize: '0.92rem', fontWeight: 500 }}>
                  Wishing you a wonderful year ahead. Thank you for being part of AXO People.
                </p>
              </div>
              <button
                onClick={closeBirthdayCelebration}
                style={{
                  border: 'none',
                  borderRadius: '10px',
                  padding: '0.55rem 0.85rem',
                  background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                  color: 'white',
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Celebrate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ zIndex: 999, marginBottom: '1.6rem', animation: 'slideIn 0.45s ease-out' }}>
        <div className="glass-effect" style={{
          borderRadius: '1.2rem',
          padding: '1.6rem',
          boxShadow: '0 8px 32px rgba(14, 165, 233, 0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.6rem', flexWrap: 'wrap' }}>
            {/* Profile Picture */}
            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute',
                inset: '-6px',
                background: `linear-gradient(135deg, ${THEME_PRIMARY} 0%, ${THEME_SECONDARY} 100%)`,
                borderRadius: '50%',
                animation: 'pulse 2.5s ease-in-out infinite',
                zIndex: 0,
                opacity: 0.9
              }}></div>
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt={`${employee.name}'s profile`}
                  style={{
                    width: '110px',
                    height: '110px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '4px solid white',
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
                  }}
                  onError={() => setProfileImageUrl(null)}
                />
              ) : (
                <div style={{
                  width: '110px',
                  height: '110px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${THEME_PRIMARY} 0%, ${THEME_SECONDARY} 100%)`,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: '700',
                  border: '4px solid white',
                  position: 'relative',
                  zIndex: 1,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                }}>
                  {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                </div>
              )}
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: employee.status?.toLowerCase() === 'active' ? '#10B981' : '#9CA3AF',
                border: '3px solid white',
                zIndex: 2,
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.12)',
                animation: employee.status?.toLowerCase() === 'active' ? 'pulse 2s ease-in-out infinite' : 'none'
              }}></div>
            </div>

            {/* Info + rate cards */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
                <div>
                  <h1 className="gradient-text" style={{
                    fontSize: '1.6rem',
                    fontWeight: '800',
                    marginBottom: '0.2rem',
                    letterSpacing: '-0.02em'
                  }}>
                    {employee.name}
                  </h1>
                  <p style={{ fontSize: '0.95rem', color: isDark ? '#94a3b8' : '#4B5563', fontWeight: '500', margin: 0 }}>
                    {/* ✅ Uses the new display string logic */}
                    {employee.position}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
                  {[
                    { type: 'lateness-over-10', label: 'Lateness ≥ 10m', value: latenessOver10Rate, icon: AlertCircle },
                    { type: 'lateness', label: 'Lateness', value: latenessRate, icon: Clock },
                    { type: 'sickness', label: 'Sickness', value: sicknessRate, icon: Activity },
                    { type: 'personal', label: 'Personal', value: personalRate, icon: User }
                  ].map((item) => {
                    const isHigh = (item.value || 0) > 2;
                    const cardColor = isDark
                      ? (isHigh ? '#fca5a5' : '#34d399')
                      : (isHigh ? '#991B1B' : '#065F46');
                    const cardBg = isDark
                      ? (isHigh ? 'linear-gradient(135deg, #3f1415, #4c1d1d)' : 'linear-gradient(135deg, #14412a, #165333)')
                      : (isHigh ? 'linear-gradient(135deg, #FEE2E2, #FECACA)' : 'linear-gradient(135deg, #D1FAE5, #A7F3D0)');
                    const borderColor = isHigh ? '#EF4444' : '#10B981';
                    const shadowColor = isHigh ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)';

                    return (
                      <div key={item.type} className="animated-card" style={{
                        background: cardBg,
                        border: `1.5px solid ${borderColor}`,
                        borderRadius: '0.75rem',
                        padding: '0.5rem 0.9rem',
                        minWidth: '110px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                        alignItems: 'flex-start',
                        boxShadow: `0 6px 18px ${shadowColor}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <item.icon size={14} color={cardColor} />
                          <p style={{ fontSize: '0.68rem', color: cardColor, margin: 0, fontWeight: '600' }}>{item.label}</p>
                        </div>
                        <p style={{ fontSize: '1.05rem', fontWeight: '700', color: cardColor, margin: 0 }}>
                          {ratesLoading ? '...' : <><AnimatedCounter value={item.value} />%</>}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="collapsible-group" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.9rem', marginTop: '0.6rem' }}>
                {[
                  { icon: Mail, label: 'Email', value: employee.user?.email },
                  { icon: Phone, label: 'Phone', value: employee.phone },
                  { icon: MapPin, label: 'Office', value: employee.office },
                  { icon: Calendar, label: 'Join Date', value: formatDate(employee.joinDate) },
                  { icon: CreditCard, label: 'CNSS', value: employee.cnss },
                  { icon: CreditCard, label: 'CIN', value: employee.cin },
                  { icon: User, label: 'Gender', value: employee.gender },
                  { icon: Calendar, label: 'Date of Birth', value: formatDate(employee.dob) },
                  { icon: Award, label: 'Diploma', value: employee.diploma },
                  { icon: Briefcase, label: 'Company', value: employee.company },
                ].map((item, idx) => (
                  <div key={idx} className="animated-card glass-effect" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                    padding: '0.65rem',
                    borderRadius: '0.6rem',
                    border: '1px solid rgba(255,255,255,0.5)'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '0.5rem',
                      background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <item.icon size={16} color="white" />
                    </div>
                    <div>
                      <p style={{ fontSize: '0.65rem', color: isDark ? '#94a3b8' : '#6B7280', marginBottom: '0.08rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {item.label}
                      </p>
                      <p style={{ fontSize: '0.85rem', fontWeight: '600', color: isDark ? '#f8fafc' : '#111827', margin: 0 }}>
                        {item.value || 'N/A'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-effect" style={{
        borderRadius: '1rem',
        padding: '0.6rem',
        marginBottom: '1.4rem',
        boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)',
        display: 'flex',
        gap: '0.6rem',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'attendance', icon: Clock, label: 'Attendance', badge: null },
          { id: 'documents', icon: FileText, label: 'Documents', badge: documents.length },
          { id: 'contract', icon: Briefcase, label: 'Contract', badge: null },
          { id: 'evaluation', icon: TrendingUp, label: 'Evaluation', badge: null },
          { id: 'career', icon: Award, label: 'Career', badge: null },
          { id: 'projects', icon: Sparkles, label: 'Projects & Roles', badge: null },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="animated-card"
            style={{
              padding: '0.6rem 1rem',
              border: 'none',
              borderRadius: '0.6rem',
              background: activeTab === tab.id
                ? `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`
                : (isDark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.7)'),
              color: activeTab === tab.id ? 'white' : (isDark ? '#cbd5e1' : '#4B5563'),
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              boxShadow: activeTab === tab.id ? `0 6px 18px ${THEME_PRIMARY}30` : 'none',
              fontSize: '0.86rem',
              backdropFilter: 'blur(8px)'
            }}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.badge !== null && tab.badge > 0 && (
              <span style={{
                backgroundColor: activeTab === tab.id ? 'rgba(255, 255, 255, 0.2)' : (isDark ? '#334155' : '#E5E7EB'),
                color: activeTab === tab.id ? 'white' : (isDark ? '#cbd5e1' : '#374151'),
                borderRadius: '9999px',
                padding: '0.08rem 0.35rem',
                fontSize: '0.68rem',
                fontWeight: '700',
                marginLeft: '0.4rem'
              }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ animation: 'slideIn 0.45s ease-out' }}>
        {activeTab === 'attendance' && (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '0.9rem',
              marginBottom: '1.4rem'
            }}>
              {[
                { type: 'ANNUAL_LEAVE', emoji: '🏖️', label: 'Vacation Days left ', value: vacationDaysLeft, max: 22, gradient: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`, color: THEME_SECONDARY },
                { type: 'SICKNESS', emoji: '🤒', label: 'Sick Days', value: sickDays, max: 10, gradient: 'linear-gradient(135deg, #EF4444, #DC2626)', color: '#991B1B' },
                { type: 'PERSONAL', emoji: '🎯', label: 'Personal ', value: personalDays, max: 15, gradient: `linear-gradient(135deg, ${SKY_PRIMARY}, ${SKY_SECONDARY})`, color: SKY_SECONDARY },
                { type: 'LATENESS', emoji: '⏳', label: 'Lateness', value: latenessDays, max: 10, gradient: 'linear-gradient(135deg, #F59E0B, #D97706)', color: '#92400E' },
              ].map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => handleOpenAbsenceModal(item.type)}
                  onMouseEnter={() => setHoveredCard(idx)}
                  onMouseLeave={() => setHoveredCard(null)}
                  className="glass-effect animated-card"
                  style={{
                    borderRadius: '1rem',
                    padding: '1.2rem',
                    cursor: 'pointer',
                    width: '100%',
                    minWidth: '200px',
                    border: '1.8px solid transparent',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '-40%',
                    right: '-40%',
                    width: '160%',
                    height: '160%',
                    background: item.gradient,
                    opacity: hoveredCard === idx ? 0.07 : 0,
                    transition: 'opacity 0.35s',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                  }}></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.6rem', position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '0.6rem',
                      background: item.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
                      animation: hoveredCard === idx ? 'float 2s ease-in-out infinite' : 'none'
                    }}>
                      {item.emoji}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '0.72rem', color: isDark ? '#9ca3af' : '#6B7280', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {item.label}
                      </h3>
                      <p style={{ fontSize: '1.25rem', fontWeight: '700', color: item.color, margin: 0 }}>
                        {item.value !== null ? <AnimatedCounter value={item.value} duration={1200} /> : '...'}
                      </p>
                    </div>
                  </div>
                  <div style={{ width: '100%', height: '10px', backgroundColor: isDark ? '#374151' : '#E5E7EB', borderRadius: '9999px', overflow: 'hidden', position: 'relative', zIndex: 1 }}>
                    <div style={{
                      width: `${item.value !== null ? Math.min((item.value / item.max) * 100, 100) : 0}%`,
                      height: '100%',
                      background: item.gradient,
                      borderRadius: '9999px',
                      transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}></div>
                  </div>
                  <p style={{ fontSize: '0.68rem', color: '#9CA3AF', marginTop: '0.6rem', textAlign: 'right', fontWeight: '500', position: 'relative', zIndex: 1 }}>
                    {item.value !== null ? `${Math.round((item.value / item.max) * 100)}% used` : ''}
                  </p>
                </div>
              ))}
            </div>

            <div className="glass-effect" style={{
              borderRadius: '1rem',
              padding: '1.6rem',
              boxShadow: '0 8px 32px rgba(14, 165, 233, 0.06)'
            }}>
              <h2 className="gradient-text" style={{ fontSize: '1.05rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <TrendingUp size={20} /> Time Overview
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="animated-card" style={{
                  padding: '1rem',
                  borderRadius: '0.75rem',
                  background: `linear-gradient(135deg, rgba(14,165,233,0.06) 0%, rgba(147,197,253,0.06) 100%)`,
                  border: `1.5px solid rgba(14,165,233,0.12)`
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '0.6rem',
                    background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '0.6rem'
                  }}>
                    <Calendar size={18} color="white" />
                  </div>
                  <p style={{ fontSize: '0.72rem', color: isDark ? '#9ca3af' : '#6B7280', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Time Employed
                  </p>
                  <p className="gradient-text" style={{ fontSize: '1.1rem', fontWeight: '800' }}>
                    {employee.timeOverview?.formattedDuration || 'N/A'}
                  </p>
                  <p style={{ fontSize: '0.72rem', color: isDark ? '#64748b' : '#9CA3AF', marginTop: '0.4rem', fontWeight: '500' }}>
                    <AnimatedCounter value={employee.timeOverview?.workedDays || 0} duration={1500} /> total days
                  </p>
                </div>
                <div className="animated-card" style={{
                  padding: '1rem',
                  borderRadius: '0.75rem',
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, rgba(52, 211, 153, 0.06) 100%)',
                  border: '1.5px solid rgba(16, 185, 129, 0.12)'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '0.6rem',
                    background: 'linear-gradient(135deg, #10B981, #059669)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '0.6rem'
                  }}>
                    <CheckCircle size={18} color="white" />
                  </div>
                  <p style={{ fontSize: '0.72rem', color: isDark ? '#9ca3af' : '#6B7280', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Actual Days Worked
                  </p>
                  <p style={{ fontSize: '1.1rem', fontWeight: '800', color: '#10B981' }}>
                    <AnimatedCounter value={employee.timeOverview?.actualWorkedDays || 0} duration={1500} />
                  </p>
                  <p style={{ fontSize: '0.72rem', color: isDark ? '#64748b' : '#9CA3AF', marginTop: '0.4rem', fontWeight: '500' }}>
                    Workdays only
                  </p>
                </div>
              </div>
            </div>

            <AbsenceRecordsModal
              open={showAbsenceModal}
              onClose={handleCloseAbsenceModal}
              employeeId={null}
              absenceType={selectedAbsenceType}
              employeeName={employee.name}
              userRole="EMPLOYEE"
            />
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            <div className="glass-effect" style={{
              borderRadius: '1rem',
              padding: '1.4rem',
              marginBottom: '1.2rem',
              boxShadow: '0 8px 32px rgba(14,165,233,0.06)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.8rem', marginBottom: '0.8rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.72rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Document Type
                  </label>
                  <select
                    value={selectedDocumentType}
                    onChange={(e) => setSelectedDocumentType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `1.5px solid ${isDark ? '#475569' : '#E5E7EB'}`,
                      borderRadius: '0.6rem',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      background: isDark ? '#1e293b' : 'white',
                      color: isDark ? '#f8fafc' : 'inherit',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <option value="All">All Documents</option>
                    <option value="Contract">Contract</option>
                    <option value="National ID">National ID</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Payroll">Payroll</option>
                    <option value="Personal Documents">Personal Documents</option>
                    <option value="Code of Conduct">Code of Conduct</option>
                    <option value="Probation Period Agreement">Probation Period Agreement</option>
                    <option value="Bank Details Form">Bank Details Form</option>
                    <option value="Promotion Letters">Promotion Letters</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.72rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Search Documents
                  </label>
                  <input
                    type="text"
                    placeholder="Search by description..."
                    value={docSearchTerm}
                    onChange={(e) => setDocSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `1.5px solid ${isDark ? '#475569' : '#E5E7EB'}`,
                      borderRadius: '0.6rem',
                      fontSize: '0.85rem',
                      background: isDark ? '#1e293b' : 'white',
                      color: isDark ? '#f8fafc' : 'inherit',
                      transition: 'all 0.2s'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.8rem', borderTop: `1px solid ${isDark ? '#334155' : '#E5E7EB'}` }}>
                <span style={{ fontSize: '0.82rem', color: isDark ? '#9ca3af' : '#6B7280', fontWeight: '600' }}>
                  Showing <span className="gradient-text" style={{ fontWeight: '800', fontSize: '0.95rem' }}>{filteredDocuments.length}</span> document{filteredDocuments.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '0.9rem'
            }}>
              {filteredDocuments.length > 0 ? (
                filteredDocuments.map((doc, idx) => (
                  <div
                    key={doc.id}
                    className="glass-effect animated-card"
                    style={{
                      borderRadius: '0.9rem',
                      padding: '1.2rem',
                      borderLeft: '4px solid',
                      borderImage: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY}) 1`,
                      animation: `slideIn ${0.2 + idx * 0.06}s ease-out`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '0.6rem',
                        background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '0.6rem'
                      }}>
                        <FileText size={20} color="white" />
                      </div>
                      <span style={{
                        background: `linear-gradient(135deg, #DBEAFE, #BFDBFE)`,
                        color: '#1E40AF',
                        padding: '0.35rem 0.6rem',
                        borderRadius: '9999px',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em'
                      }}>{doc.type}</span>
                    </div>
                    <h3 style={{ fontSize: '0.98rem', fontWeight: '700', color: '#111827', lineHeight: '1.4' }}>
                      {doc.description || 'Untitled Document'}
                    </h3>
                  </div>
                ))
              ) : (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '2rem',
                }} className="glass-effect">
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    opacity: 0.25
                  }}>
                    <FileText size={34} color="white" />
                  </div>
                  <p style={{ fontSize: '1rem', color: '#6B7280', fontWeight: '600' }}>
                    No {selectedDocumentType === 'All' ? '' : selectedDocumentType.toLowerCase()} documents found.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contract */}
        {activeTab === 'contract' && (
          <div className="glass-effect" style={{
            borderRadius: '1rem',
            padding: '1.6rem',
            boxShadow: '0 8px 32px rgba(14,165,233,0.06)'
          }}>
            <h2 className="gradient-text" style={{ fontSize: '1.05rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Briefcase size={20} /> Contract Details
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.9rem' }}>
              {[
                { label: 'Type of Contract', value: contract?.type, icon: FileText },
                { label: 'Start Date', value: formatDate(contract?.startDate), icon: Calendar },
                { label: 'Status', value: contract?.status, icon: Activity },
                { label: 'Work Hours', value: contract?.workHours ? `${contract.workHours} hours/week` : 'N/A', icon: Clock },
              ].map((item, idx) => (
                <div key={idx} className="animated-card" style={{
                  padding: '0.95rem',
                  borderRadius: '0.75rem',
                  background: `linear-gradient(135deg, rgba(14,165,233,0.04) 0%, rgba(118,75,162,0.04) 100%)`,
                  border: `1.5px solid rgba(14,165,233,0.08)`
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '0.6rem',
                    background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <item.icon size={18} color="white" />
                  </div>
                  <p style={{ fontSize: '0.72rem', color: '#6B7280', marginBottom: '0.25rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {item.label}
                  </p>
                  <p style={{ fontSize: '0.95rem', fontWeight: '700', color: '#111827' }}>
                    {item.value || 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Evaluation */}
        {activeTab === 'evaluation' && (
          <div className="glass-effect" style={{
            borderRadius: '1rem',
            padding: '1.6rem',
            boxShadow: '0 8px 32px rgba(14,165,233,0.06)'
          }}>
            <h2 className="gradient-text" style={{ fontSize: '1.05rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={20} /> Employee Evaluation
            </h2>
            <Evaluation />
          </div>
        )}

        {/* Career */}
        {activeTab === 'career' && (
          <div style={{
            backgroundColor: isDark ? '#1e293b' : 'white',
            borderRadius: '1rem', padding: '1.6rem',
            boxShadow: '0 8px 32px rgba(14,165,233,0.06)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{ fontSize: '1.05rem', fontWeight: '800', color: isDark ? '#f8fafc' : '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={20} color="#3B82F6" /> Career History
                </h2>
                <p style={{ fontSize: '0.875rem', color: isDark ? '#94a3b8' : '#6B7280', margin: '0.25rem 0 0' }}>
                  Position history, projects, and career milestones
                </p>
              </div>
              <button
                onClick={() => {
                  if (showCareerForm) {
                    handleCareerCancel();
                  } else {
                    setEditingCareer(null);
                    setCareerForm(emptyCareerForm);
                    setShowCareerForm(true);
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.6rem 1.25rem', backgroundColor: showCareerForm && !editingCareer ? '#6B7280' : '#3B82F6',
                  color: 'white', border: 'none', borderRadius: '0.6rem',
                  fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer',
                }}
              >
                <Plus size={16} />
                {showCareerForm && !editingCareer ? 'Cancel' : 'Add Entry'}
              </button>
            </div>

            {careersError && (
              <div style={{ color: '#EF4444', marginBottom: '0.9rem', padding: '0.85rem', backgroundColor: '#FEE2E2', borderRadius: '0.8rem', border: '1.5px solid #EF4444', fontWeight: '600', fontSize: '0.9rem' }}>
                {careersError}
              </div>
            )}

            {showCareerForm && (
              <div style={{
                backgroundColor: isDark ? '#1e293b' : '#F8FAFF',
                border: `1px solid ${editingCareer ? '#8B5CF6' : '#3B82F6'}`,
                borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1.5rem',
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: editingCareer ? '#7C3AED' : '#2563EB', marginBottom: '1.25rem', marginTop: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {editingCareer ? <Edit2 size={16} /> : <Plus size={16} />}
                  {editingCareer ? 'Edit Career Record' : 'New Career Entry'}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Position Title *</label>
                    <select name="positionTitle"
                      value={CAREER_POSITION_OPTIONS.includes(careerForm.positionTitle) ? careerForm.positionTitle : '__custom__'}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setCareerForm(prev => ({ ...prev, positionTitle: '' }));
                        } else {
                          setCareerForm(prev => ({ ...prev, positionTitle: e.target.value }));
                        }
                      }}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: isDark ? '#1e293b' : 'white', color: isDark ? '#f1f5f9' : '#111827', boxSizing: 'border-box' }}
                    >
                      <option value="">-- Select position --</option>
                      {CAREER_POSITION_OPTIONS.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                      <option value="__custom__">Custom title...</option>
                    </select>
                    {(!CAREER_POSITION_OPTIONS.includes(careerForm.positionTitle) || careerForm.positionTitle === '') && (
                      <input name="positionTitle" value={careerForm.positionTitle} onChange={handleCareerFormChange} placeholder="Type custom position title"
                        style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: isDark ? '#1e293b' : 'white', color: isDark ? '#f1f5f9' : '#111827', boxSizing: 'border-box', marginTop: '0.4rem' }} />
                    )}
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Department</label>
                    <select name="department" value={careerForm.department} onChange={handleCareerFormChange}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: isDark ? '#1e293b' : 'white', color: isDark ? '#f1f5f9' : '#111827', boxSizing: 'border-box' }}
                    >
                      <option value="">-- Select department --</option>
                      {allDepartments.map(d => (
                        <option key={d.departmentId || d.id || d.name} value={d.name || d.departmentName}>
                          {d.name || d.departmentName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Employment Type</label>
                    <select name="employmentType" value={careerForm.employmentType} onChange={handleCareerFormChange}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: isDark ? '#1e293b' : 'white', color: isDark ? '#f1f5f9' : '#111827', boxSizing: 'border-box' }}
                    >
                      {EMPLOYMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Start Date *</label>
                    <input type="date" name="startDate" value={careerForm.startDate} onChange={handleCareerFormChange}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: isDark ? '#1e293b' : 'white', color: isDark ? '#f1f5f9' : '#111827', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#6B7280', display: 'block', marginBottom: '0.25rem' }}>End Date <span style={{ fontWeight: 400, color: isDark ? '#64748b' : '#9CA3AF' }}>(leave blank if current)</span></label>
                    <input type="date" name="endDate" value={careerForm.endDate} onChange={handleCareerFormChange}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem', fontSize: '0.875rem', backgroundColor: isDark ? '#1e293b' : 'white', color: isDark ? '#f1f5f9' : '#111827', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Projects <span style={{ fontWeight: 400, color: isDark ? '#64748b' : '#9CA3AF' }}>(select all that apply)</span></label>
                    <div style={{
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem',
                      backgroundColor: isDark ? '#1e293b' : 'white', maxHeight: '140px', overflowY: 'auto', padding: '0.5rem'
                    }}>
                      {CAREER_PROJECT_OPTIONS.map(name => {
                        const checked = (careerForm.projectNames || '').split(',').map(s => s.trim()).filter(Boolean).includes(name);
                        return (
                          <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.2rem 0.3rem', cursor: 'pointer', fontSize: '0.8rem', color: isDark ? '#f1f5f9' : '#111827' }}>
                            <input type="checkbox" checked={checked} onChange={(e) => {
                              const current = (careerForm.projectNames || '').split(',').map(s => s.trim()).filter(Boolean);
                              const updated = e.target.checked
                                ? [...current.filter(n => n !== name), name]
                                : current.filter(n => n !== name);
                              setCareerForm(prev => ({ ...prev, projectNames: updated.join(', ') }));
                            }} style={{ accentColor: '#3B82F6' }} />
                            {name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#6B7280', display: 'block', marginBottom: '0.25rem' }}>Description / Responsibilities</label>
                  <RichTextEditor
                    value={careerForm.description || ''}
                    onChange={(val) => handleCareerFormChange({ target: { name: 'description', value: val } })}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button onClick={handleCareerCancel}
                    style={{ padding: '0.5rem 1.25rem', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`, borderRadius: '0.5rem', cursor: 'pointer', color: isDark ? '#94a3b8' : '#6B7280', fontWeight: 600, fontSize: '0.875rem' }}>
                    Cancel
                  </button>
                  <button onClick={handleCareerSubmit}
                    disabled={!careerForm.positionTitle.trim() || !careerForm.startDate}
                    style={{ padding: '0.5rem 1.25rem', backgroundColor: editingCareer ? '#7C3AED' : '#3B82F6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', opacity: (!careerForm.positionTitle.trim() || !careerForm.startDate) ? 0.6 : 1 }}>
                    {editingCareer ? 'Save Changes' : 'Add Entry'}
                  </button>
                </div>
              </div>
            )}

            {careersLoading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: isDark ? '#94a3b8' : '#6B7280' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '0.5rem' }} />
                <p>Loading career history...</p>
              </div>
            ) : careers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: isDark ? '#94a3b8' : '#6B7280' }}>
                <TrendingUp size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                <p style={{ fontSize: '1rem', fontWeight: '600', color: isDark ? '#f8fafc' : '#111827', marginBottom: '0.5rem' }}>No career records yet</p>
                <p style={{ fontSize: '0.875rem' }}>Add career entries to build your career timeline.</p>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '18px', top: '8px', bottom: '8px', width: '2px', backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB', borderRadius: '2px' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {careers.map((career) => {
                    const typeColor = typeColors[career.employmentType] || { bg: '#F3F4F6', text: '#374151' };
                    const isCurrent = !career.endDate;
                    const projects = career.projectNames ? career.projectNames.split(',').map(p => p.trim()).filter(Boolean) : [];
                    return (
                      <div key={career.id} style={{ display: 'flex', gap: '1rem', paddingLeft: '0' }}>
                        <div style={{ flexShrink: 0, width: '38px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '4px' }}>
                          <div style={{
                            width: '14px', height: '14px', borderRadius: '50%', marginTop: '6px',
                            backgroundColor: isCurrent ? '#3B82F6' : (isDark ? '#475569' : '#CBD5E1'),
                            border: `3px solid ${isCurrent ? '#BFDBFE' : (isDark ? '#1e293b' : 'white')}`,
                            boxShadow: isCurrent ? '0 0 0 3px rgba(59,130,246,0.2)' : 'none',
                            zIndex: 1, position: 'relative',
                          }} />
                        </div>
                        <div style={{
                          flex: 1, backgroundColor: isDark ? '#1e293b' : 'white', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB'}`,
                          borderRadius: '0.75rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        }}>
                          <div style={{ padding: '1rem 1.25rem', backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#F0F0F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: '700', color: isDark ? '#f1f5f9' : '#111827', margin: 0 }}>{career.positionTitle}</h3>
                                {isCurrent && (
                                  <span style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8', fontSize: '0.65rem', fontWeight: '700', padding: '0.2rem 0.5rem', borderRadius: '9999px', letterSpacing: '0.04em' }}>CURRENT</span>
                                )}
                                <span style={{ backgroundColor: typeColor.bg, color: typeColor.text, fontSize: '0.7rem', fontWeight: '600', padding: '0.2rem 0.6rem', borderRadius: '9999px' }}>{career.employmentType || 'Full-time'}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                {career.department && (
                                  <span style={{ fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#6B7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <Building2 size={13} /> {career.department}
                                  </span>
                                )}
                                <span style={{ fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#6B7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <CalendarDays size={13} />
                                  {career.startDate ? formatDate(career.startDate) : '?'} — {isCurrent ? <span style={{ color: '#10B981', fontWeight: '600' }}>Present</span> : formatDate(career.endDate)}
                                </span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button onClick={() => handleCareerEdit(career)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6', padding: '4px 6px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}
                                title="Edit">
                                <Edit2 size={15} />
                              </button>
                              <button onClick={() => handleCareerDelete(career.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '4px 6px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}
                                title="Delete">
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                          {(career.description || projects.length > 0) && (
                            <div style={{ padding: '1rem 1.25rem' }}>
                              {career.description && (
                                <div style={{ fontSize: '0.875rem', color: isDark ? '#94a3b8' : '#6B7280', lineHeight: '1.6', margin: 0 }} dangerouslySetInnerHTML={{ __html: career.description }} />
                              )}
                              {projects.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: career.description ? '0.75rem' : 0 }}>
                                  <FolderOpen size={14} color="#6B7280" />
                                  {projects.map((proj, i) => (
                                    <span key={i} style={{ backgroundColor: isDark ? 'rgba(99,102,241,0.15)' : '#EEF2FF', color: isDark ? '#a5b4fc' : '#4338CA', fontSize: '0.75rem', fontWeight: '500', padding: '0.2rem 0.6rem', borderRadius: '9999px', border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#C7D2FE'}` }}>
                                      {proj}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Projects & Positions */}
        {activeTab === 'projects' && (
          <div className="glass-effect" style={{
            borderRadius: '1rem',
            padding: '1.6rem',
            boxShadow: '0 8px 32px rgba(14,165,233,0.06)'
          }}>
            <h2 className="gradient-text" style={{ fontSize: '1.05rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Sparkles size={20} /> Manage Roles & Projects
            </h2>
            {projectsError && (
              <div style={{
                color: '#EF4444',
                marginBottom: '0.9rem',
                padding: '0.85rem',
                backgroundColor: '#FEE2E2',
                borderRadius: '0.8rem',
                border: '1.5px solid #EF4444',
                fontWeight: '600',
                fontSize: '0.9rem'
              }}>
                Error: {projectsError}
              </div>
            )}
            {saveSuccess && (
              <div style={{
                color: '#059669',
                marginBottom: '0.9rem',
                padding: '0.85rem',
                backgroundColor: '#D1FAE5',
                borderRadius: '0.8rem',
                border: '1.5px solid #059669',
                fontWeight: '600',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <CheckCircle size={16} /> Successfully updated profile!
              </div>
            )}

            {/* Positions Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.6rem', fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Professional Roles (Multi-Position)
              </label>
              {positions.map((pos, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <select
                    value={pos.positionName}
                    onChange={(e) => handlePositionChange(idx, 'positionName', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: `1.5px solid ${isDark ? '#475569' : '#E5E7EB'}`,
                      borderRadius: '0.6rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      background: isDark ? '#1e293b' : 'white',
                      color: isDark ? '#f8fafc' : 'inherit'
                    }}
                  >
                    <option value="">Select Position</option>
                    {POSITION_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <select
                    value={pos.fte}
                    onChange={(e) => handlePositionChange(idx, 'fte', Number(e.target.value))}
                    style={{
                      width: '100px',
                      padding: '0.75rem',
                      border: `1.5px solid ${isDark ? '#475569' : '#E5E7EB'}`,
                      borderRadius: '0.6rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      background: isDark ? '#1e293b' : 'white',
                      color: isDark ? '#f8fafc' : 'inherit'
                    }}
                  >
                    {FTE_OPTIONS.map(fte => (
                      <option key={fte} value={fte}>{fte}</option>
                    ))}
                  </select>
                  {positions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePosition(idx)}
                      style={{
                        padding: '0 0.8rem',
                        background: '#EF4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.6rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Minus size={16} />
                    </button>
                  )}
                </div>
              ))}

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={handleAddPosition}
                  disabled={positions.length >= 4}
                  style={{
                    background: positions.length >= 4 ? '#9CA3AF' : 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.6rem',
                    padding: '0.4rem 0.8rem',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: positions.length >= 4 ? 'not-allowed' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Plus size={14} /> Add Role
                </button>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#cbd5e1' : '#4B5563' }}>
                  Total FTE: <span style={{ color: isFTEValid(positions) ? '#10B981' : '#EF4444' }}>{calculateTotalFTE(positions).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Projects Section */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.45rem', fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Primary Project
              </label>
              <select
                value={projects.project1}
                onChange={(e) => handleProjectChange('project1', e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1.5px solid ${isDark ? '#475569' : '#E5E7EB'}`,
                  borderRadius: '0.6rem',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  background: isDark ? '#1e293b' : 'white',
                  color: isDark ? '#f8fafc' : 'inherit',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {projectOptions.map(option => (
                  <option key={`p1-${option}`} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {showProject2 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.45rem', fontSize: '0.75rem', fontWeight: '600', color: isDark ? '#94a3b8' : '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Additional Projects
                </label>
                <div style={{
                  border: `1.5px solid ${isDark ? '#334155' : '#E5E7EB'}`,
                  borderRadius: '0.6rem',
                  padding: '1rem',
                  background: isDark ? 'rgba(30, 41, 59, 0.6)' : 'rgba(249, 250, 251, 0.6)'
                }}>
                  {projectOptions
                    .filter(option => option !== 'N/A')
                    .map(option => (
                      <label key={`additional-${option}`} className="animated-card" style={{
                        display: 'inline-block',
                        margin: '0.35rem',
                        padding: '0.4rem 0.9rem',
                        borderRadius: '9999px',
                        background: selectedAdditionalProjects.includes(option)
                          ? `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`
                          : (isDark ? '#334155' : '#E5E7EB'),
                        color: selectedAdditionalProjects.includes(option) ? 'white' : (isDark ? '#cbd5e1' : '#374151'),
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.86rem',
                        boxShadow: selectedAdditionalProjects.includes(option)
                          ? `0 6px 18px ${THEME_PRIMARY}25`
                          : 'none'
                      }}>
                        <input
                          type="checkbox"
                          value={option}
                          checked={selectedAdditionalProjects.includes(option)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedAdditionalProjects(prev =>
                              e.target.checked
                                ? [...prev, value]
                                : prev.filter(item => item !== value)
                            );
                          }}
                          style={{ display: 'none' }}
                        />
                        {option}
                      </label>
                    ))}
                </div>
                <div style={{ marginTop: '0.6rem', fontSize: '0.86rem', color: isDark ? '#9ca3af' : '#6B7280', fontWeight: '600' }}>
                  Selected: <span className="gradient-text" style={{ fontWeight: '800' }}>
                    {selectedAdditionalProjects.length > 0
                      ? selectedAdditionalProjects.join(' - ')
                      : 'None'}
                  </span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  if (!showProject2) {
                    const currentProjects = projects.project2 && projects.project2 !== 'N/A'
                      ? projects.project2.split('-').filter(p => p.trim() !== '')
                      : [];
                    setSelectedAdditionalProjects(currentProjects);
                  } else {
                    const combinedProjects = selectedAdditionalProjects.join('-');
                    setProjects(prev => ({ ...prev, project2: combinedProjects || 'N/A' }));
                  }
                  setShowProject2(!showProject2);
                }}
                className="animated-card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  padding: '0.6rem 1rem',
                  border: `1.5px solid ${isDark ? '#475569' : '#E5E7EB'}`,
                  borderRadius: '0.6rem',
                  background: isDark ? '#1e293b' : 'white',
                  color: isDark ? '#f8fafc' : '#374151',
                  cursor: 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: '600'
                }}
              >
                {showProject2 ? <Trash2 size={16} /> : <Plus size={16} />}
                {showProject2 ? 'Remove Additional' : 'Add More Projects'}
              </button>
              <button
                onClick={handleSaveProjects}
                disabled={isProjectsLoading}
                className="animated-card"
                style={{
                  padding: '0.6rem 1.1rem',
                  background: isProjectsLoading
                    ? 'linear-gradient(135deg, #93C5FD, #BFDBFE)'
                    : `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.6rem',
                  cursor: isProjectsLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.92rem',
                  boxShadow: isProjectsLoading ? 'none' : `0 6px 18px ${THEME_PRIMARY}25`
                }}
              >
                {isProjectsLoading ? (
                  <div style={{ animation: 'spin 1s linear infinite' }}>
                    <Loader2 size={16} color="white" />
                  </div>
                ) : (
                  <CheckCircle size={16} color="white" />
                )}
                {isProjectsLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDashboard;
